import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  Meeting,
  MeetingFilterOption,
  MeetingRequest,
  MeetingResponse,
  MeetingSearchParams,
  PageResponse,
  MeetingType,
  MeetingStatus,
  AvailabilityApi,
  MeetingParticipantRequest,
  MeetingParticipantResponse,
  MeetingAgendaItemRequest,
  MeetingAgendaItemResponse,
  MeetingReminderRequest,
  MeetingReminderResponse,
  MeetingRescheduleLogRequest,
  MeetingRescheduleLogResponse,
  MeetingAuditLogResponse,
} from '../models/meeting.model';

@Injectable({
  providedIn: 'root',
})
export class MeetingApiService {
  private apiUrl = `${environment.apiUrl}api`;

  constructor(private http: HttpClient) {}

  // ---------------------------------------------------------------
  // /api/meetings/filters — INC 68: opciones acotadas a los involucrados.
  // businessId se toma del JWT, sin query params.
  // ---------------------------------------------------------------

  getFilterContacts(): Observable<MeetingFilterOption[]> {
    return this.http.get<MeetingFilterOption[]>(`${this.apiUrl}/meetings/filters/contacts`);
  }
  getFilterCompanies(): Observable<MeetingFilterOption[]> {
    return this.http.get<MeetingFilterOption[]>(`${this.apiUrl}/meetings/filters/companies`);
  }
  getFilterDeals(): Observable<MeetingFilterOption[]> {
    return this.http.get<MeetingFilterOption[]>(`${this.apiUrl}/meetings/filters/deals`);
  }
  getFilterOrganizers(): Observable<MeetingFilterOption[]> {
    return this.http.get<MeetingFilterOption[]>(`${this.apiUrl}/meetings/filters/organizers`);
  }

  // ---------------------------------------------------------------
  // /api/meetings
  // ---------------------------------------------------------------

  searchMeetings(
    params: MeetingSearchParams
  ): Observable<PageResponse<MeetingResponse>> {
    let p = new HttpParams().set('businessId', params.businessId.toString());

    const num = (k: string, v?: number) => {
      if (v !== undefined && v !== null) p = p.set(k, v.toString());
    };
    const str = (k: string, v?: string) => {
      if (v !== undefined && v !== null && v !== '') p = p.set(k, v);
    };
    const bool = (k: string, v?: boolean) => {
      if (v !== undefined && v !== null) p = p.set(k, v.toString());
    };

    num('idMeetingType', params.idMeetingType);
    num('idMeetingStatus', params.idMeetingStatus);
    num('contactId', params.contactId);
    num('companyId', params.companyId);
    num('dealId', params.dealId);
    num('organizerId', params.organizerId);

    // Preferir parámetros nuevos; aceptar aliases legacy
    str('datetimeStartFrom', params.datetimeStartFrom ?? params.dateFrom);
    str('datetimeStartTo', params.datetimeStartTo ?? params.dateTo);
    str('searchTitle', params.searchTitle ?? params.searchTerm);
    bool('upcomingOnly', params.upcomingOnly);
    bool('pastOnly', params.pastOnly);
    bool('isNoShow', params.isNoShow);
    str('paymentStatus', params.paymentStatus);
    str('source', params.source);
    // INC 74 — alcance del listado (MINE/ALL); el userId sale del JWT.
    str('scope', params.scope);

    num('page', params.page);
    num('size', params.size);
    str('sortBy', params.sortBy);
    str('sortDirection', params.sortDirection);

    return this.http.get<PageResponse<MeetingResponse>>(
      `${this.apiUrl}/meetings`,
      { params: p }
    );
  }

  getMeetingById(id: number, businessId: number): Observable<MeetingResponse> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.get<MeetingResponse>(`${this.apiUrl}/meetings/${id}`, {
      params,
    });
  }

  createMeeting(meeting: MeetingRequest): Observable<MeetingResponse> {
    return this.http.post<MeetingResponse>(`${this.apiUrl}/meetings`, meeting);
  }

  updateMeeting(
    id: number,
    businessId: number,
    meeting: MeetingRequest
  ): Observable<MeetingResponse> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.put<MeetingResponse>(
      `${this.apiUrl}/meetings/${id}`,
      meeting,
      { params }
    );
  }

  deleteMeeting(id: number, businessId: number): Observable<void> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.delete<void>(`${this.apiUrl}/meetings/${id}`, { params });
  }

  getUpcomingMeetings(businessId: number): Observable<MeetingResponse[]> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.get<MeetingResponse[]>(
      `${this.apiUrl}/meetings/upcoming`,
      { params }
    );
  }

  getPastMeetings(businessId: number): Observable<MeetingResponse[]> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.get<MeetingResponse[]>(`${this.apiUrl}/meetings/past`, {
      params,
    });
  }

  getMeetingsByDeal(
    dealId: number,
    businessId: number
  ): Observable<MeetingResponse[]> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.get<MeetingResponse[]>(
      `${this.apiUrl}/meetings/by-deal/${dealId}`,
      { params }
    );
  }

  getMeetingsByCompany(
    companyId: number,
    businessId: number
  ): Observable<MeetingResponse[]> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.get<MeetingResponse[]>(
      `${this.apiUrl}/meetings/by-company/${companyId}`,
      { params }
    );
  }

  getMeetingsByContact(
    contactId: number,
    businessId: number
  ): Observable<MeetingResponse[]> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.get<MeetingResponse[]>(
      `${this.apiUrl}/meetings/by-contact/${contactId}`,
      { params }
    );
  }

  getMeetingsByDateRange(
    businessId: number,
    start: string,
    end: string,
    scope: 'MINE' | 'ALL' = 'ALL'
  ) {
    let params = new HttpParams()
      .set('businessId', businessId.toString())
      .set('datetimeStartFrom', start)
      .set('datetimeStartTo', end)
      .set('size', '1000');
    // INC 74 — alcance del calendario (MINE/ALL); el userId sale del JWT.
    if (scope) params = params.set('scope', scope);
    return this.http.get<PageResponse<MeetingResponse>>(
      `${this.apiUrl}/meetings`,
      { params }
    );
  }

  // ---------------------------------------------------------------
  // Sub-recursos: Participants
  // ---------------------------------------------------------------

  listParticipants(idMeeting: number): Observable<MeetingParticipantResponse[]> {
    return this.http.get<MeetingParticipantResponse[]>(
      `${this.apiUrl}/meetings/${idMeeting}/participants`
    );
  }
  getParticipant(
    idMeeting: number,
    id: number
  ): Observable<MeetingParticipantResponse> {
    return this.http.get<MeetingParticipantResponse>(
      `${this.apiUrl}/meetings/${idMeeting}/participants/${id}`
    );
  }
  createParticipant(
    idMeeting: number,
    body: MeetingParticipantRequest
  ): Observable<MeetingParticipantResponse> {
    return this.http.post<MeetingParticipantResponse>(
      `${this.apiUrl}/meetings/${idMeeting}/participants`,
      body
    );
  }
  updateParticipant(
    idMeeting: number,
    id: number,
    body: MeetingParticipantRequest
  ): Observable<MeetingParticipantResponse> {
    return this.http.put<MeetingParticipantResponse>(
      `${this.apiUrl}/meetings/${idMeeting}/participants/${id}`,
      body
    );
  }
  deleteParticipant(idMeeting: number, id: number): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/meetings/${idMeeting}/participants/${id}`
    );
  }
  updateParticipantStatus(
    idMeeting: number,
    id: number,
    status: string,
    notes?: string
  ): Observable<MeetingParticipantResponse> {
    let params = new HttpParams().set('status', status);
    if (notes) params = params.set('notes', notes);
    return this.http.patch<MeetingParticipantResponse>(
      `${this.apiUrl}/meetings/${idMeeting}/participants/${id}/status`,
      null,
      { params }
    );
  }

  // ---------------------------------------------------------------
  // Sub-recursos: Agenda
  // ---------------------------------------------------------------

  listAgenda(idMeeting: number): Observable<MeetingAgendaItemResponse[]> {
    return this.http.get<MeetingAgendaItemResponse[]>(
      `${this.apiUrl}/meetings/${idMeeting}/agenda`
    );
  }
  getAgendaItem(
    idMeeting: number,
    id: number
  ): Observable<MeetingAgendaItemResponse> {
    return this.http.get<MeetingAgendaItemResponse>(
      `${this.apiUrl}/meetings/${idMeeting}/agenda/${id}`
    );
  }
  createAgendaItem(
    idMeeting: number,
    body: MeetingAgendaItemRequest
  ): Observable<MeetingAgendaItemResponse> {
    return this.http.post<MeetingAgendaItemResponse>(
      `${this.apiUrl}/meetings/${idMeeting}/agenda`,
      body
    );
  }
  updateAgendaItem(
    idMeeting: number,
    id: number,
    body: MeetingAgendaItemRequest
  ): Observable<MeetingAgendaItemResponse> {
    return this.http.put<MeetingAgendaItemResponse>(
      `${this.apiUrl}/meetings/${idMeeting}/agenda/${id}`,
      body
    );
  }
  deleteAgendaItem(idMeeting: number, id: number): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/meetings/${idMeeting}/agenda/${id}`
    );
  }

  // ---------------------------------------------------------------
  // Sub-recursos: Reminders
  // ---------------------------------------------------------------

  listReminders(idMeeting: number): Observable<MeetingReminderResponse[]> {
    return this.http.get<MeetingReminderResponse[]>(
      `${this.apiUrl}/meetings/${idMeeting}/reminders`
    );
  }
  getReminder(
    idMeeting: number,
    id: number
  ): Observable<MeetingReminderResponse> {
    return this.http.get<MeetingReminderResponse>(
      `${this.apiUrl}/meetings/${idMeeting}/reminders/${id}`
    );
  }
  createReminder(
    idMeeting: number,
    body: MeetingReminderRequest
  ): Observable<MeetingReminderResponse> {
    return this.http.post<MeetingReminderResponse>(
      `${this.apiUrl}/meetings/${idMeeting}/reminders`,
      body
    );
  }
  updateReminder(
    idMeeting: number,
    id: number,
    body: MeetingReminderRequest
  ): Observable<MeetingReminderResponse> {
    return this.http.put<MeetingReminderResponse>(
      `${this.apiUrl}/meetings/${idMeeting}/reminders/${id}`,
      body
    );
  }
  deleteReminder(idMeeting: number, id: number): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/meetings/${idMeeting}/reminders/${id}`
    );
  }

  // ---------------------------------------------------------------
  // Historial: audit logs de la reunión
  // ---------------------------------------------------------------

  listAuditLogs(
    meetingId: number,
    businessId: number
  ): Observable<MeetingAuditLogResponse[]> {
    const params = new HttpParams()
      .set('businessId', businessId.toString())
      .set('entityType', 'meeting')
      .set('entityId', meetingId.toString());
    return this.http.get<MeetingAuditLogResponse[]>(
      `${this.apiUrl}/meeting-audit-logs/by-entity`,
      { params }
    );
  }

  // ---------------------------------------------------------------
  // Sub-recursos: Reschedule logs
  // ---------------------------------------------------------------

  listRescheduleLogs(
    meetingId: number,
    page = 0,
    size = 20
  ): Observable<PageResponse<MeetingRescheduleLogResponse>> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());
    return this.http.get<PageResponse<MeetingRescheduleLogResponse>>(
      `${this.apiUrl}/meetings/${meetingId}/reschedule-logs`,
      { params }
    );
  }
  getRescheduleLog(
    meetingId: number,
    id: number
  ): Observable<MeetingRescheduleLogResponse> {
    return this.http.get<MeetingRescheduleLogResponse>(
      `${this.apiUrl}/meetings/${meetingId}/reschedule-logs/${id}`
    );
  }
  createRescheduleLog(
    meetingId: number,
    body: MeetingRescheduleLogRequest
  ): Observable<MeetingRescheduleLogResponse> {
    return this.http.post<MeetingRescheduleLogResponse>(
      `${this.apiUrl}/meetings/${meetingId}/reschedule-logs`,
      body
    );
  }

  // ===============================================================
  // DEPRECATED — catálogos y availability siguen aquí para no
  // romper componentes existentes. Usar los servicios dedicados:
  //  - MeetingTypeApiService
  //  - MeetingStatusApiService
  //  - MeetingUserAvailabilityApiService
  // ===============================================================

  /** @deprecated use MeetingTypeApiService */
  getMeetingTypes(
    businessId: number,
    _activeOnly = true
  ): Observable<MeetingType[]> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.get<MeetingType[]>(`${this.apiUrl}/meeting-types/active`, {
      params,
    });
  }
  /** @deprecated use MeetingTypeApiService */
  getMeetingTypeById(id: number, businessId: number): Observable<MeetingType> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.get<MeetingType>(`${this.apiUrl}/meeting-types/${id}`, {
      params,
    });
  }
  /** @deprecated use MeetingStatusApiService */
  getMeetingStatuses(
    businessId: number,
    _activeOnly = true
  ): Observable<MeetingStatus[]> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.get<MeetingStatus[]>(
      `${this.apiUrl}/meeting-statuses/active`,
      { params }
    );
  }
  /** @deprecated use MeetingStatusApiService */
  getMeetingStatusById(
    id: number,
    businessId: number
  ): Observable<MeetingStatus> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.get<MeetingStatus>(
      `${this.apiUrl}/meeting-statuses/${id}`,
      { params }
    );
  }

  /** @deprecated use MeetingUserAvailabilityApiService */
  getAvailabilityByBusiness(businessId: number): Observable<AvailabilityApi[]> {
    return this.http.get<AvailabilityApi[]>(
      `${this.apiUrl}/meeting-user-availability/business/${businessId}`
    );
  }
  /** @deprecated */
  getAvailabilityByUser(userId: number): Observable<AvailabilityApi[]> {
    return this.http.get<AvailabilityApi[]>(
      `${this.apiUrl}/meeting-user-availability/user/${userId}`
    );
  }
  /** @deprecated */
  saveAvailability(data: AvailabilityApi) {
    return this.http.post(`${this.apiUrl}/meeting-user-availability`, data);
  }
  /** @deprecated */
  updateAvailability(
    id: number,
    data: AvailabilityApi
  ): Observable<AvailabilityApi> {
    return this.http.put<AvailabilityApi>(
      `${this.apiUrl}/meeting-user-availability/${id}`,
      data
    );
  }
  /** @deprecated */
  deleteAvailability(id: number): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/meeting-user-availability/${id}`
    );
  }
  /** @deprecated */
  getAvailability(businessId: number): Observable<AvailabilityApi[]> {
    return this.getAvailabilityByBusiness(businessId);
  }
  /** @deprecated */
  getAvailabilityByUserAndBusiness(userId: number, businessId: number) {
    return this.http.get<AvailabilityApi[]>(
      `${this.apiUrl}/meeting-user-availability/user/${userId}/business/${businessId}`
    );
  }

  /** @deprecated alias legacy — devuelve array con size grande */
  getMeetingsByDateRangeLegacy(
    businessId: number,
    start: string,
    end: string
  ): Observable<PageResponse<Meeting>> {
    return this.http.get<PageResponse<Meeting>>(`${this.apiUrl}/meetings`, {
      params: {
        businessId,
        datetimeStartFrom: start,
        datetimeStartTo: end,
        size: 1000,
      },
    });
  }
}
